<script setup lang="ts">
    import { computed } from 'vue';
    import type {
        AnnotationGroup,
        HipSpineAnnotationCategory,
    } from '@/views/3d-planner/hipSpine/types';
    import { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';
    import { useHipSpineSyncStore } from '@/views/3d-planner/hipSpine/hipSpineSyncStore';
    import {
        useHipSpineAnnotationHelpViewModel,
    } from '@/views/3d-planner/hipSpine/annotationHelp/useHipSpineAnnotationHelpViewModel';
    import { LUMBAR_LEVELS, type LumbarLevel } from '@/domain/anatomy/hipSpineLandmarks';

    const props = defineProps<{
        posture: string;
        category: string;
        categoryKey: HipSpineAnnotationCategory;
        colour: string;
        groups: AnnotationGroup[];
    }>();

    const editVm = useHipSpineXRayEditViewModel();
    const sync = useHipSpineSyncStore();
    const helpVm = useHipSpineAnnotationHelpViewModel();
    const lumbarLevels = LUMBAR_LEVELS;

    // The lumbar section renders a single end-plate row with a level selector —
    // never one row per group — so the selector cannot be duplicated even if more
    // than one lumbar group ever reaches this section. undefined for non-lumbar
    // sections, which render one row per group instead.
    const lumbarGroup = computed(() =>
        props.categoryKey === 'lumbar' ? props.groups[0] : undefined,
    );

    const selectedLumbarLevel = computed<LumbarLevel>({
        get: () => editVm.selectedLumbarLevel(props.posture),
        set: (level) => editVm.setLumbarLevel(props.posture, level),
    });

    // A section flags "not visible" as soon as any one of its annotation groups
    // is toggled off — e.g. hiding either PF1 or PF2 means a landmark is hidden.
    // Visibility intent is owned by the ViewModel so it survives geometry rebuilds.
    const sectionHidden = computed(() =>
        props.groups.some((group) => editVm.isGroupHidden(props.posture, group)),
    );

    function toggleGroup(group: AnnotationGroup): void {
        editVm.toggleGroupVisibility(props.posture, group);
    }
</script>

<template>
    <div class="annotation">
        <div class="annotation-heading">
            <span class="annotation-category-name">{{ category }}</span>
            <div class="d-flex ga-2">
                <v-chip
                    v-if="sectionHidden"
                    size="x-small"
                    variant="tonal"
                    class="annotation-not-visible"
                >
                    Landmark Not Visible
                </v-chip>
                <v-icon
                    icon="mdi-help-circle-outline"
                    size="16"
                    class="annotation-help"
                    :data-testid="`hip-spine-help-${categoryKey}`"
                    @click="helpVm.openHelp(categoryKey)"
                />
            </div>
        </div>

        <div v-if="lumbarGroup" class="annotation-sub-group">
            <span class="annotation-colour-line" :style="{ backgroundColor: colour }" />
            <span class="annotation-sub-group-title">Lumbar Vertebra End Plate</span>
            <v-select
                v-model="selectedLumbarLevel"
                :items="lumbarLevels"
                density="compact"
                variant="outlined"
                hide-details
                :disabled="sync.isSaving"
                class="annotation-lumbar-select"
            />
            <v-icon
                :icon="editVm.isGroupHidden(posture, lumbarGroup) ? 'mdi-eye-off' : 'mdi-eye'"
                size="small"
                class="annotation-eye"
                @click="toggleGroup(lumbarGroup)"
            />
        </div>

        <template v-else>
            <div v-for="group in groups" :key="group.label" class="annotation-sub-group">
                <span class="annotation-colour-line" :style="{ backgroundColor: colour }" />
                <span class="annotation-sub-group-title">{{ group.name }}</span>
                <v-icon
                    :icon="editVm.isGroupHidden(posture, group) ? 'mdi-eye-off' : 'mdi-eye'"
                    size="small"
                    class="annotation-eye"
                    @click="toggleGroup(group)"
                />
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
    .annotation {
        margin-bottom: 4px;
        border: 1px solid rgba(var(--v-theme-outline));
        border-radius: 6px;
        overflow: hidden;

        &-heading {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 6px 8px;
            background-color: rgba(var(--v-theme-surface-light));
        }

        &-category-name {
            font-size: 16px;
            color: rgba(var(--v-theme-on-surface));
        }

        &-not-visible {
            margin-left: auto;
            color: rgba(var(--v-theme-red-hover));
            background-color: rgba(var(--v-theme-red-hover), 0.21);
        }

        &-sub-group {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 8px;
        }

        &-colour-line {
            display: inline-block;
            width: 16px;
            height: 3px;
            border-radius: 2px;
            flex-shrink: 0;
        }

        &-sub-group-title {
            font-size: 13px;
            color: rgba(var(--v-theme-secondary-text));
            flex-grow: 1;
        }

        &-lumbar-select {
            max-width: 84px;
            flex-shrink: 0;
        }

        &-eye {
            cursor: pointer;
            opacity: 0.7;
            flex-shrink: 0;

            &:hover {
                opacity: 1;
            }
        }

        &-help {
            margin-left: auto;
            cursor: pointer;
            opacity: 0.7;
            flex-shrink: 0;

            &:hover {
                opacity: 1;
            }
        }
    }
</style>
